import React, { useContext, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { AuthContext } from "../../contexts/AuthContext";
import phoneCodes from "../../utils/static/phoneCodes";
import {
  ContactFields,
  OrganizationFields,
  PasswordFields,
} from "./RegisterForm/RegistrationFields";
import PromotionCodeField from "./RegisterForm/PromotionCodeField";
import RegistrationComplete from "./RegisterForm/RegistrationComplete";
import {
  buildMicrosoftRegistrationDraft,
  buildRegistrationPayload,
  getRegistrationFeedback,
  getRegistrationSchema,
  getRegistrationSteps,
  readPromoCodeParam,
} from "./RegisterForm/registrationHelpers";
import AuthFeedback from "./utils/AuthFeedback";
import MicrosoftAuthButton from "./utils/MicrosoftAuthButton";

const RegistrationForm = ({
  invitationToken,
  setMessage,
  email,
  name,
  adminEmail,
}) => {
  const { register, startMicrosoftSSO, resendVerificationEmail } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invited = Boolean(invitationToken);
  const promoFromUrl = invited ? "" : readPromoCodeParam(searchParams);
  const steps = getRegistrationSteps(invited);
  const [stepIndex, setStepIndex] = useState(0);
  const [validating, setValidating] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const sectionRef = useRef(null);
  const previousStep = useRef(stepIndex);
  const activeStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const handleRegistrationError = (error, microsoft = false) => {
    const errorFeedback = getRegistrationFeedback(error, microsoft);
    setFeedback(errorFeedback);
    if (errorFeedback.returnToStep === "contact") setStepIndex(0);
  };

  const formik = useFormik({
    initialValues: {
      name: name || "",
      email: email || "",
      phoneCode: phoneCodes[0].code,
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      orgName: "",
      orgGSTNo: "",
      promoCode: promoFromUrl,
    },
    validationSchema: getRegistrationSchema(invited),
    onSubmit: async (values) => {
      if (ssoLoading) return;
      setFeedback(null);
      setMessage?.(null);
      try {
        const payload = buildRegistrationPayload(values, invited);
        await register(payload);
        setRegisteredEmail(payload.email);
      } catch (error) {
        handleRegistrationError(error);
      }
    },
  });
  const { setFieldValue } = formik;
  const formDisabled = validating || formik.isSubmitting || ssoLoading;

  // Sync invitation details without changing Formik values during rendering.
  useEffect(() => {
    if (name) setFieldValue("name", name, false);
  }, [name, setFieldValue]);
  useEffect(() => {
    if (email) setFieldValue("email", email, false);
  }, [email, setFieldValue]);
  useEffect(() => {
    if (previousStep.current !== stepIndex) {
      sectionRef.current?.focus();
      previousStep.current = stepIndex;
    }
  }, [stepIndex]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formDisabled) return;
    setValidating(true);
    try {
      const errors = await formik.validateForm();
      const fields = isLastStep
        ? steps.flatMap((item) => item.fields)
        : activeStep.fields;
      await formik.setTouched(
        {
          ...formik.touched,
          ...Object.fromEntries(fields.map((field) => [field, true])),
        },
        false
      );
      const invalidField = fields.find((field) => errors[field]);
      if (invalidField) {
        const invalidStep = steps.findIndex((item) =>
          item.fields.includes(invalidField)
        );
        setStepIndex(invalidStep);
        requestAnimationFrame(() => {
          document.getElementById(`register-${invalidField}`)?.focus();
        });
        return;
      }
      setFeedback(null);
      setMessage?.(null);
      if (isLastStep) {
        await formik.submitForm();
      } else {
        setStepIndex((current) => current + 1);
      }
    } finally {
      setValidating(false);
    }
  };

  const handleMicrosoftRegister = async () => {
    if (formDisabled || invited) return;
    setFeedback(null);
    setMessage?.(null);
    setSsoLoading(true);
    try {
      await startMicrosoftSSO(
        "register",
        buildMicrosoftRegistrationDraft(formik.values)
      );
    } catch (error) {
      setSsoLoading(false);
      handleRegistrationError(error, true);
    }
  };

  if (registeredEmail) {
    return (
      <RegistrationComplete
        email={registeredEmail}
        onResend={resendVerificationEmail}
        onSignIn={() => {
          setMessage?.(null);
          navigate("/auth?request_type=login", { replace: true });
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        mx: "auto",
        "& .MuiButton-root": { textTransform: "none", borderRadius: 2 },
        "& .MuiOutlinedInput-root": { borderRadius: 2 },
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography component='h1' variant='h5' fontWeight={700}>
          {invited ? "Join your team on Tax Savy" : "Get started with Tax Savy"}
        </Typography>

        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          {invited
            ? "Complete your account setup to access your organization's tax workspace."
            : "Create your account and set up your organization's tax workspace."}
        </Typography>

        {adminEmail && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 1, overflowWrap: "anywhere" }}
          >
            Invited by {adminEmail}
          </Typography>
        )}
      </Box>

      <AuthFeedback feedback={feedback} sx={{ mb: 2.5 }} />

      <Box
        component='form'
        noValidate
        onSubmit={handleSubmit}
        aria-busy={formDisabled}
      >
        <Stack spacing={2.5}>
          <Box
            key={activeStep.key}
            ref={sectionRef}
            tabIndex={-1}
            sx={{ outline: "none" }}
          >
            {activeStep.key === "contact" && (
              <Stack spacing={3}>
                <ContactFields
                  formik={formik}
                  disabled={formDisabled}
                  nameLocked={Boolean(name)}
                  emailLocked={Boolean(email)}
                />
                <PasswordFields formik={formik} disabled={formDisabled} />
              </Stack>
            )}
            {activeStep.key === "organization" && (
              <Stack spacing={2.5}>
                <OrganizationFields formik={formik} disabled={formDisabled} />
                <PromotionCodeField formik={formik} disabled={formDisabled} />
              </Stack>
            )}
          </Box>
          <Stack direction='row' spacing={1.5}>
            {stepIndex > 0 && (
              <Button
                type='button'
                variant='outlined'
                disabled={formDisabled}
                onClick={() => {
                  setFeedback(null);
                  setStepIndex((current) => current - 1);
                }}
                sx={{ minHeight: 48, px: 3 }}
              >
                Back
              </Button>
            )}
            <Button
              type='submit'
              variant='contained'
              disabled={formDisabled}
              startIcon={
                formik.isSubmitting ? (
                  <CircularProgress size={18} color='inherit' />
                ) : undefined
              }
              sx={{
                flex: 1,
                minHeight: 48,
                boxShadow: "none",
                fontWeight: 600,
              }}
            >
              {formik.isSubmitting
                ? "Creating account..."
                : isLastStep
                  ? "Create account"
                  : "Next"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {!invited && (
        <MicrosoftAuthButton
          disabled={formDisabled}
          loading={ssoLoading}
          onClick={handleMicrosoftRegister}
        />
      )}
    </Box>
  );
};

export default RegistrationForm;
